import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ChatMessage,
  WorldviewChatRequest,
  WorldviewChatResponse,
  WorldviewRefineRequest,
} from '@arena/shared';
import { SceneBack } from '@/shared/components/SceneBack';
import { DiffView } from '../components/DiffView';
import { HistoryModal } from '../components/HistoryModal';
import {
  type SavedWorldview,
  DEFAULT_CHAT_SETTINGS,
  addRevision,
  getCurrentContent,
  getCurrentVersion,
  getWorldview,
} from '../lib/storage';

// Same-origin by default; Vite proxies /api/* to wrangler dev. Avoids
// cross-origin permission prompts on Safari / macOS / firewalls.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

interface RefineState {
  openIdx: number | null;
  draft: string;
  /** Awaiting first byte from the server. */
  submitting: boolean;
  /** Stream has started; partial content is accumulating. */
  streaming: boolean;
  /** Partial revised file as it arrives over the stream. */
  streamedContent: string;
  error: string | null;
  /** When set, the LLM has finished. Awaiting user approval. */
  proposedContent: string | null;
}

/**
 * Parse an SSE wire-format stream chunk into discrete events. The buffer
 * may contain multiple `event: …\n data: …\n\n` blocks; returns the parsed
 * events plus whatever incomplete tail remains for the next read.
 */
function parseSseEvents(
  buffer: string,
): { events: Array<{ event: string; data: string }>; rest: string } {
  const events: Array<{ event: string; data: string }> = [];
  let rest = buffer;
  for (;;) {
    const sep = rest.indexOf('\n\n');
    if (sep < 0) break;
    const raw = rest.slice(0, sep);
    rest = rest.slice(sep + 2);
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    events.push({ event, data: dataLines.join('\n') });
  }
  return { events, rest };
}

const REFINE_STATUS_MESSAGES = [
  'Reading your feedback…',
  "Reasoning about what's missing…",
  'Rewriting your worldview…',
  'Almost there…',
];

const REFINE_STATUS_INTERVAL_MS = 3500;

interface RevisionMarker {
  /** Message index after which this banner appears. */
  afterIdx: number;
  version: number;
}

export function Chat() {
  const { id } = useParams<{ id: string }>();
  const [worldview, setWorldview] = useState<SavedWorldview | undefined>(() =>
    id ? getWorldview(id) : undefined,
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refineState, setRefineState] = useState<RefineState>({
    openIdx: null,
    draft: '',
    submitting: false,
    streaming: false,
    streamedContent: '',
    error: null,
    proposedContent: null,
  });
  const [revisions, setRevisions] = useState<RevisionMarker[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [refineStatusIdx, setRefineStatusIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const streamingRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!refineState.submitting) {
      setRefineStatusIdx(0);
      return;
    }
    const interval = window.setInterval(() => {
      setRefineStatusIdx((i) => Math.min(i + 1, REFINE_STATUS_MESSAGES.length - 1));
    }, REFINE_STATUS_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [refineState.submitting]);

  useEffect(() => {
    const el = streamingRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [refineState.streamedContent]);

  useEffect(() => {
    document.title = 'Test Your Worldview — The Arena';
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, refineState.openIdx, revisions]);

  if (!worldview) {
    return (
      <main className="scene">
        <div className="scene__image scene__image--worldview" role="img" aria-label="Library" />
        <div className="scene__vignette" />
        <SceneBack />
        <div className="scene__content">
          <p className="scene__eyebrow">— Missing —</p>
          <h1 className="scene__title">Worldview not found</h1>
          <p className="scene__subtitle">
            It may have been cleared from this browser. Return to the library to craft another.
          </p>
          <Link to="/worldview" className="scene__cta">
            Return
          </Link>
        </div>
      </main>
    );
  }

  const currentContent = getCurrentContent(worldview);
  const currentVersion = getCurrentVersion(worldview).version;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError(null);
    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setSending(true);

    try {
      const body: WorldviewChatRequest = {
        worldviewContent: currentContent,
        messages: next,
        settings: worldview.settings ?? DEFAULT_CHAT_SETTINGS,
      };
      const res = await fetch(`${API_BASE}/api/worldview/chat`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
        };
        throw new Error(payload.detail ?? payload.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as WorldviewChatResponse;
      setMessages((m) => [...m, data.message]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openRefine = (idx: number) =>
    setRefineState({
      openIdx: idx,
      draft: '',
      submitting: false,
      streaming: false,
      streamedContent: '',
      error: null,
      proposedContent: null,
    });
  const closeRefine = () =>
    setRefineState({
      openIdx: null,
      draft: '',
      submitting: false,
      streaming: false,
      streamedContent: '',
      error: null,
      proposedContent: null,
    });

  const tryAgain = () =>
    setRefineState((s) => ({
      ...s,
      proposedContent: null,
      streamedContent: '',
      error: null,
    }));

  const submitRefine = async (assistantIdx: number) => {
    const draft = refineState.draft.trim();
    if (!draft || refineState.submitting || refineState.streaming) return;
    const question = messages[assistantIdx - 1]?.content ?? '';
    const response = messages[assistantIdx]?.content ?? '';
    setRefineState((s) => ({
      ...s,
      submitting: true,
      streaming: false,
      streamedContent: '',
      error: null,
      proposedContent: null,
    }));

    try {
      const body: WorldviewRefineRequest = {
        currentContent,
        question,
        response,
        feedback: draft,
      };
      const res = await fetch(`${API_BASE}/api/worldview/refine`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          accept: 'text/event-stream',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
        };
        throw new Error(payload.detail ?? payload.error ?? `HTTP ${res.status}`);
      }
      if (!res.body) {
        throw new Error('empty_response_body');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let firstChunk = true;
      let serverError: string | null = null;
      let done = false;

      while (!done) {
        const read = await reader.read();
        if (read.done) break;
        buffer += decoder.decode(read.value, { stream: true });
        const parsed = parseSseEvents(buffer);
        buffer = parsed.rest;
        for (const evt of parsed.events) {
          if (evt.event === 'chunk') {
            try {
              const { text } = JSON.parse(evt.data) as { text: string };
              accumulated += text;
              if (firstChunk) {
                firstChunk = false;
                setRefineState((s) => ({
                  ...s,
                  submitting: false,
                  streaming: true,
                  streamedContent: accumulated,
                }));
              } else {
                setRefineState((s) =>
                  s.streaming ? { ...s, streamedContent: accumulated } : s,
                );
              }
            } catch {
              // ignore malformed chunk
            }
          } else if (evt.event === 'error') {
            try {
              const { detail } = JSON.parse(evt.data) as { detail: string };
              serverError = detail;
            } catch {
              serverError = evt.data || 'stream_error';
            }
            done = true;
            break;
          } else if (evt.event === 'done') {
            done = true;
            break;
          }
        }
      }

      if (serverError) {
        throw new Error(serverError);
      }

      setRefineState((s) => ({
        ...s,
        streaming: false,
        streamedContent: '',
        proposedContent: accumulated.trim(),
      }));
    } catch (err) {
      setRefineState((s) => ({
        ...s,
        submitting: false,
        streaming: false,
        streamedContent: '',
        error: err instanceof Error ? err.message : 'Refine failed',
      }));
    }
  };

  const confirmSave = (assistantIdx: number) => {
    const proposed = refineState.proposedContent;
    const draft = refineState.draft.trim();
    if (!proposed || !worldview) return;
    const question = messages[assistantIdx - 1]?.content ?? '';
    const response = messages[assistantIdx]?.content ?? '';
    const updated = addRevision(worldview.id, proposed, {
      question,
      response,
      note: draft,
    });
    if (updated) {
      setWorldview(updated);
      const newVersion = getCurrentVersion(updated).version;
      setRevisions((r) => [...r, { afterIdx: assistantIdx, version: newVersion }]);
    }
    closeRefine();
  };

  return (
    <main className="scene scene--chat">
      <div className="scene__image scene__image--worldview" role="img" aria-label="Library" />
      <div className="scene__vignette" />
      <SceneBack />

      <div className="chat">
        <div className="chat__header">
          <p className="chat__eyebrow">— Training Ground —</p>
          <h1 className="chat__title">Train your worldview.</h1>
          <p className="chat__subtitle">Ask it anything. Agree, disagree, correct it. Every correction sharpens the model.</p>
          <button
            type="button"
            className="chat__history-link"
            onClick={() => setHistoryOpen(true)}
          >
            History · v{currentVersion}
          </button>
        </div>

        <div className="chat__messages" ref={scrollRef}>
          {messages.length === 0 && (
            <p className="chat__empty">No messages yet. Send one below.</p>
          )}

          {messages.map((m, i) => {
            const revision = revisions.find((r) => r.afterIdx === i);
            const isRefining = refineState.openIdx === i;
            return (
              <div key={i} className="chat__row">
                <div className={`chat__msg chat__msg--${m.role}`}>
                  <span className="chat__role">
                    {m.role === 'user' ? 'You' : 'Your worldview'}
                  </span>
                  <p className="chat__content">{m.content}</p>
                  {m.role === 'assistant' && !isRefining && (
                    <button
                      type="button"
                      className="chat__refine-toggle"
                      onClick={() => openRefine(i)}
                    >
                      Refine <span aria-hidden="true">↗</span>
                    </button>
                  )}
                </div>

                {isRefining && (
                  <div
                    className={`chat__refine${refineState.submitting ? ' chat__refine--thinking' : ''}${refineState.streaming ? ' chat__refine--streaming' : ''}${refineState.proposedContent ? ' chat__refine--preview' : ''}`}
                    role="region"
                    aria-label="Refine response"
                  >
                    {refineState.submitting ? (
                      <div
                        className="chat__refine-thinking"
                        role="status"
                        aria-live="polite"
                      >
                        <p className="chat__refine-thinking__title">
                          Updating worldview
                        </p>
                        <div
                          className="chat__refine-thinking__dots"
                          aria-hidden="true"
                        >
                          <span className="chat__dot" />
                          <span className="chat__dot" />
                          <span className="chat__dot" />
                        </div>
                        <p
                          className="chat__refine-thinking__status"
                          key={refineStatusIdx}
                        >
                          {REFINE_STATUS_MESSAGES[refineStatusIdx]}
                        </p>
                        <p className="chat__refine-thinking__hint">
                          Editing the generators — this usually takes 5–15 seconds.
                        </p>
                      </div>
                    ) : refineState.streaming ? (
                      <div
                        className="chat__streaming"
                        role="status"
                        aria-live="polite"
                      >
                        <div className="chat__streaming-header">
                          <p className="chat__streaming-title">
                            Writing the new worldview
                          </p>
                          <p className="chat__streaming-meta">
                            {refineState.streamedContent.length.toLocaleString()} chars
                          </p>
                        </div>
                        <pre className="chat__streaming-body" ref={streamingRef}>
                          {refineState.streamedContent}
                          <span className="chat__streaming-caret" aria-hidden="true" />
                        </pre>
                      </div>
                    ) : refineState.proposedContent ? (
                      <div className="chat__preview">
                        <div className="chat__preview-header">
                          <p className="chat__preview-eyebrow">
                            — Proposed Revision —
                          </p>
                          <h3 className="chat__preview-title">
                            Review before saving
                          </h3>
                          <p className="chat__preview-feedback">
                            <span className="chat__preview-feedback-label">
                              Based on your feedback:
                            </span>{' '}
                            &ldquo;{refineState.draft}&rdquo;
                          </p>
                        </div>

                        <DiffView
                          before={currentContent}
                          after={refineState.proposedContent}
                        />

                        <div className="chat__preview-actions">
                          <button
                            type="button"
                            className="chat__preview-retry"
                            onClick={tryAgain}
                          >
                            <span aria-hidden="true">↻</span> Try again
                          </button>
                          <button
                            type="button"
                            className="chat__preview-save"
                            onClick={() => confirmSave(i)}
                          >
                            Save revision
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <label className="chat__refine-label" htmlFor={`refine-${i}`}>
                          What would the real you say instead?
                        </label>
                        <textarea
                          id={`refine-${i}`}
                          className="chat__refine-input"
                          value={refineState.draft}
                          onChange={(e) =>
                            setRefineState((s) => ({ ...s, draft: e.target.value }))
                          }
                          placeholder="Actually, I think…"
                          rows={3}
                          autoFocus
                        />
                        {refineState.error && (
                          <div className="chat__error" role="alert">
                            {refineState.error}
                          </div>
                        )}
                        <div className="chat__refine-actions">
                          <button
                            type="button"
                            className="chat__refine-cancel"
                            onClick={closeRefine}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="chat__refine-submit"
                            onClick={() => submitRefine(i)}
                            disabled={!refineState.draft.trim()}
                          >
                            Update worldview
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {revision && (
                  <div className="chat__updated" role="status">
                    <span aria-hidden="true">✓</span> Worldview updated to v{revision.version}
                  </div>
                )}
              </div>
            );
          })}

          {sending && (
            <div className="chat__msg chat__msg--assistant chat__msg--pending">
              <span className="chat__role">Your worldview</span>
              <p className="chat__content">
                <span className="chat__dot" />
                <span className="chat__dot" />
                <span className="chat__dot" />
              </p>
            </div>
          )}
          {error && (
            <div className="chat__error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="chat__compose">
          <textarea
            className="chat__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask anything…"
            rows={3}
            spellCheck={false}
            disabled={sending}
          />
          <button
            type="button"
            className="chat__send"
            onClick={handleSend}
            disabled={!input.trim() || sending}
          >
            Send
          </button>
        </div>
      </div>

      <HistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={worldview.versions}
      />
    </main>
  );
}
